'use client';

import { useState } from 'react';
import { UserPlus, Share2, MoreHorizontal, Calendar, MapPin, Link as LinkIcon, Check, Zap, Award, ShieldCheck, Swords, Flame, Camera, FileText, Mail, Phone, Lock, Send, Instagram, Twitter, Youtube, Linkedin, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { LevelBadge, XpProgressBar } from '@/components/XpComponents';
import VerifiedBadge, { isVerifiedTier } from '@/components/common/VerifiedBadge';
import { createClient } from '@/lib/supabase/client';
import { track } from '@/lib/analytics';
import { useLang } from '@/lib/i18n/LanguageContext';


interface ProfileHeaderProps {
  user: {
    userId: string;
    username: string;
    displayName: string;
    initial: string;
    bio: string;
    location: string;
    website: string;
    // Contact card — email/phone arrive ONLY for the owner (see /api/users/[username]).
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    socials?: Partial<Record<'telegram' | 'instagram' | 'twitter' | 'youtube' | 'tiktok' | 'linkedin', string>>;
    joinedDate: string;
    avatarUrl?: string | null;
    tradingStyle?: string | null;
    favoriteMarkets?: string | null;
    rankBadge?: string | null;
    verificationTier?: string;
    verifiedMetrics?: { provider?: string;[k: string]: any } | null;
    foundingMemberNumber?: number | null;
    uvodAttendedAt?: string | null;
    isCurrentUser: boolean;
    isFollowing: boolean;
    xp?: number;
    level?: number;
    streakDays?: number;
    stats: {
      totalTrades: number;
      openTrades: number;
      closedTrades?: number;
      winRate: number;
      followers: number;
      following: number;
      likesReceived: number;
      totalPnlR: number;
      performanceScore?: number;
      winCount?: number;
      lossCount?: number;
      beCount?: number;
      avgRR?: number;
      consistencyScore?: number;
      accuracyScore?: number;
      battlesWon?: number;
      battlesLost?: number;
      battleWinRate?: number;
    };
  };
  onFollowChange?: (isFollowing: boolean, followerCount: number) => void;
}

function getBadgeStyle(badge: string): { bg: string; color: string; border: string } {
  if (badge === 'Elite') return { bg: 'var(--win-muted)', color: 'var(--win)', border: 'var(--win)' };
  if (badge === 'Pro') return { bg: 'var(--accent-muted)', color: 'var(--accent)', border: 'var(--accent)' };
  if (badge === 'Verified') return { bg: 'var(--bullish-muted)', color: 'var(--bullish)', border: 'var(--bullish)' };
  if (badge === 'Rising') return { bg: 'var(--info-muted)', color: 'var(--info)', border: 'var(--info)' };
  return { bg: 'var(--bullish-muted)', color: 'var(--bullish)', border: 'var(--bullish)' };
}

const BADGE_TOOLTIPS: Record<string, string> = {
  Elite: '15+ closed trades with a minimum 55% win rate',
  Verified: '5–14 closed trades',
  Rising: '1–4 closed trades',
};

function getProfileBadge(closedTrades: number, winRate: number): string | null {
  if (closedTrades >= 15 && winRate >= 55) return 'Elite';
  if (closedTrades >= 5 && closedTrades < 15) return 'Verified';
  if (closedTrades >= 1 && closedTrades < 5) return 'Rising';
  return null;
}

// Social handles are stored as either a bare handle ("@nikola") or a full URL — normalize to a URL.
const SOCIALS: { key: 'telegram' | 'instagram' | 'twitter' | 'youtube' | 'tiktok' | 'linkedin'; label: string; Icon: typeof Send; base: (h: string) => string }[] = [
  { key: 'telegram', label: 'Telegram', Icon: Send, base: (h) => `https://t.me/${h}` },
  { key: 'instagram', label: 'Instagram', Icon: Instagram, base: (h) => `https://instagram.com/${h}` },
  { key: 'twitter', label: 'X', Icon: Twitter, base: (h) => `https://x.com/${h}` },
  { key: 'youtube', label: 'YouTube', Icon: Youtube, base: (h) => `https://youtube.com/@${h}` },
  { key: 'tiktok', label: 'TikTok', Icon: AtSign, base: (h) => `https://tiktok.com/@${h}` },
  { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, base: (h) => `https://linkedin.com/in/${h}` },
];
function socialUrl(value: string, base: (h: string) => string): string {
  const v = value.trim();
  if (/^https?:\/\//i.test(v)) return v;
  if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(v)) return `https://${v}`;
  return base(v.replace(/^@/, ''));
}

function getScoreColor(score: number): string {
  if (score >= 75) return 'var(--bullish)';
  if (score >= 50) return 'var(--win)';
  if (score >= 25) return 'var(--info)';
  return 'var(--text-muted)';
}

export default function ProfileHeader({ user, onFollowChange }: ProfileHeaderProps) {
  const { t } = useLang();
  const [following, setFollowing] = useState(user.isFollowing);
  const [followerCount, setFollowerCount] = useState(user.stats.followers);
  const [followLoading, setFollowLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl);
  const [avatarUploading, setAvatarUploading] = useState(false);

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t('Image must be under 5MB')); return; }
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error(t('Use JPG, PNG, or WebP')); return;
    }
    setAvatarUploading(true);
    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      const formData = new FormData();
      formData.append('file', file);
      const headers: Record<string, string> = {};
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
      const res = await fetch('/api/settings/avatar', { method: 'POST', headers, body: formData });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error ?? t('Upload failed')); }
      const data = await res.json();
      setAvatarUrl(data.avatarUrl);
      toast.success(t('Profile photo updated!'));
    } catch (err: any) {
      toast.error(err.message ?? t('Failed to upload'));
    } finally {
      setAvatarUploading(false);
    }
  }

  async function handleFollow() {
    if (followLoading) return;
    setFollowLoading(true);
    const newFollowing = !following;
    const newCount = newFollowing ? followerCount + 1 : followerCount - 1;
    setFollowing(newFollowing);
    setFollowerCount(newCount);

    try {
      const res = await fetch(`/api/users/${encodeURIComponent(user.username)}/follow`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setFollowing(data.following);
        setFollowerCount(data.followerCount);
        onFollowChange?.(data.following, data.followerCount);
        if (data.following && user.userId) track('follow_trader', { target_id: user.userId });
        toast.success(data.following ? `${t('Now following')} @${user.username}` : `${t('Unfollowed')} @${user.username}`);
      } else if (res.status === 401) {
        setFollowing(!newFollowing);
        setFollowerCount(followerCount);
        toast.error(t('Please sign in to follow traders'));
      } else {
        setFollowing(!newFollowing);
        setFollowerCount(followerCount);
        toast.error(t('Failed to update follow status'));
      }
    } catch {
      setFollowing(!newFollowing);
      setFollowerCount(followerCount);
      toast.error(t('Network error — please try again'));
    } finally {
      setFollowLoading(false);
    }
  }

  function handleShare() {
    navigator.clipboard.writeText(`${typeof window !== 'undefined' ? window.location.origin : ''}/profile/${user.username}`);
    toast.success(t('Profile link copied'));
  }

  const score = user.stats.performanceScore ?? 0;
  const avgRR = user.stats.avgRR ?? 0;
  const consistency = user.stats.consistencyScore ?? 0;
  const closedTrades = user.stats.closedTrades ?? 0;
  const computedBadge = getProfileBadge(closedTrades, user.stats.winRate);
  const badge = computedBadge || user.rankBadge;
  const badgeStyle = badge ? getBadgeStyle(badge) : null;

  // Determine if trader is "verified" based on performance
  const isVerified = score >= 60 || (user.stats.winRate >= 60 && (user.stats.totalTrades ?? 0) >= 10);

  // Favorite markets display
  const favoriteMarkets = user.favoriteMarkets
    ? user.favoriteMarkets.split(',').map((m) => m.trim()).filter(Boolean)
    : [];

  return (
    <div className="card-base overflow-hidden">
      {/* Cover gradient — warm-light forest-green banner */}
      <div className="h-36 sm:h-40 w-full relative overflow-hidden" style={{ background: 'linear-gradient(105deg, var(--accent), #0e7a52, var(--accent))' }}>
        {/* Chart lines in cover — faint cream sparkline overlay */}
        <svg className="absolute inset-0 w-full h-full opacity-25" viewBox="0 0 800 160" preserveAspectRatio="none">
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f6f4ee" stopOpacity="0" />
              <stop offset="20%" stopColor="#f6f4ee" stopOpacity="1" />
              <stop offset="80%" stopColor="#f6f4ee" stopOpacity="1" />
              <stop offset="100%" stopColor="#f6f4ee" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polyline points="0,110 60,95 120,105 180,75 240,85 300,55 360,65 420,40 480,50 540,30 600,45 660,25 720,35 800,28" fill="none" stroke="url(#chartGrad)" strokeWidth="2.5" />
          <polyline points="0,120 80,110 160,125 240,95 320,105 400,85 480,95 560,75 640,85 720,70 800,78" fill="none" stroke="#f6f4ee" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5" />
        </svg>
        {/* Win rate hero overlay for traders with data */}
        {user.stats.winRate > 0 && closedTrades > 0 && (
          <div className="absolute top-4 right-5 text-right">
            <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(246,244,238,0.55)' }}>{t('Idea Win Rate')}</div>
            <div className="text-3xl font-bold font-mono" style={{ color: 'rgba(246,244,238,0.85)' }}>
              {user.stats.winRate}%
            </div>
          </div>
        )}
        {/* Bottom fade into the card surface */}
        <div className="absolute bottom-0 left-0 right-0 h-16" style={{ background: 'linear-gradient(to top, var(--bg-card), transparent)' }} />
      </div>

      <div className="px-5 pb-5">
        {/* Avatar + actions row */}
        <div className="flex items-end justify-between mb-4">
          <div className="relative">
            {/* Avatar — 104px circle, cream cutout ring against the cover */}
            <div
              className="w-[104px] h-[104px] rounded-full flex items-center justify-center text-2xl font-bold overflow-hidden relative"
              style={{
                marginTop: -44,
                border: '5px solid var(--bg-base)',
                background: avatarUrl ? 'var(--bg-sunken)' : 'linear-gradient(135deg, var(--accent), var(--accent-hover))',
                color: avatarUrl ? 'var(--text-primary)' : '#fff',
              }}
            >
              {avatarUrl ? <img src={avatarUrl} alt={user.username} className="w-full h-full object-cover" /> : user.initial}
              {avatarUploading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-full" style={{ background: 'rgba(28,43,38,0.6)' }}>
                  <span className="w-7 h-7 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            {/* Verified seal (LIVE_VERIFIED traders only) — bottom-right, opposite the upload button */}
            {isVerifiedTier(user.verificationTier) && <VerifiedBadge size={26} className="!-bottom-0.5 !-right-0.5" />}
            {/* Upload button for current user */}
            {user.isCurrentUser && (
              <label
                className="absolute -bottom-1 -left-1 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-150"
                style={{ background: 'var(--accent)', border: '2.5px solid var(--bg-card)', boxShadow: '0 2px 8px var(--accent-glow)' }}
                title={t('Change profile photo')}
              >
                <Camera size={13} style={{ color: 'white' }} />
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleAvatarUpload}
                  disabled={avatarUploading}
                  className="hidden"
                />
              </label>
            )}
            {/* Verified badge on avatar */}
            {isVerified && (
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'var(--accent)', border: '2.5px solid var(--bg-card)', boxShadow: '0 2px 8px var(--accent-glow)' }}>
                <ShieldCheck size={13} style={{ color: 'white' }} />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pb-1">
            {user.isCurrentUser ? (
              <Link href="/user-profile?tab=settings" className="btn-ghost px-4 py-2 text-sm font-semibold">{t('Edit Profile')}</Link>
            ) : (
              <button
                onClick={handleFollow}
                disabled={followLoading}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm disabled:opacity-60 ${following ? 'btn-ghost' : 'btn-primary'}`}
                onMouseEnter={(e) => {
                  if (following) { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bearish-muted)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--bearish)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--bearish)'; }
                }}
                onMouseLeave={(e) => {
                  if (following) { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--text-dim)'; }
                }}
              >
                {following ? <><Check size={14} />{t('Following')}</> : <><UserPlus size={14} />{t('Follow')}</>}
              </button>
            )}
            <button onClick={handleShare} className="p-2 rounded-lg btn-ghost" title={t('Share profile')}><Share2 size={16} /></button>
            <button className="p-2 rounded-lg btn-ghost" title={t('More options')}><MoreHorizontal size={16} /></button>
          </div>
        </div>

        {/* Name + username + badges */}
        <div className="mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold font-heading" style={{ color: 'var(--text-primary)' }}>{user.displayName || user.username}</h1>
            {user.verificationTier === 'LIVE_VERIFIED' && (
              user.verifiedMetrics?.provider === 'ONCHAIN_HL' ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--bullish-muted)', color: 'var(--bullish)', border: '1px solid var(--bullish)' }} title={t('Verified trustlessly from on-chain (Hyperliquid) history')}>
                  {t('⛓️ Live-verified (on-chain)')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--bullish-muted)', color: 'var(--bullish)', border: '1px solid var(--bullish)' }} title={t('Verified from a real connected account (beta)')}>
                  <ShieldCheck size={10} />
                  {t('Live-Verified')}
                </span>
              )
            )}
            {user.verificationTier === 'STATEMENT_VERIFIED' && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--win-muted)', color: 'var(--win)', border: '1px solid var(--win)' }} title={t('Statement reviewed and verified by our team')}>
                <FileText size={10} />
                {t('Statement-verified')}
              </span>
            )}
            {isVerified && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid var(--accent)' }}>
                <ShieldCheck size={10} />
                {t('Verified Trader')}
              </span>
            )}
            {badge && badgeStyle && (
              <span
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold cursor-help"
                style={{ background: badgeStyle.bg, color: badgeStyle.color, border: `1px solid ${badgeStyle.border}` }}
                title={BADGE_TOOLTIPS[badge] ? t(BADGE_TOOLTIPS[badge]) : badge}
              >
                <Award size={10} />
                {t(badge)}
              </span>
            )}
            {user.foundingMemberNumber && (
              <span
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: 'var(--win-muted)', color: 'var(--win)', border: '1px solid var(--win)' }}
                title={t('One of the first 100 traders to post here')}
              >
                <Award size={10} />
                {t('Founding Member')} #{user.foundingMemberNumber}
              </span>
            )}
            {user.uvodAttendedAt && (
              <span
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid var(--accent)' }}
                title={t('Attended the FX Doctor Uvod presentation')}
              >
                <Award size={10} />
                {t('Uvod attendee')}
              </span>
            )}
            {user.level !== undefined && user.level > 1 && (
              <LevelBadge level={user.level} size="md" showTitle />
            )}
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            @{user.username}
            {(user.firstName || user.lastName) && (
              <span style={{ color: 'var(--text-secondary)' }}> · {[user.firstName, user.lastName].filter(Boolean).join(' ')}</span>
            )}
          </p>
        </div>

        {/* Contact card — owner only (email/phone never leave the server for other viewers) */}
        {user.isCurrentUser && (
          <div className="mb-4 p-3 rounded-xl flex flex-col gap-1.5" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{t('Contact')}</span>
              <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}><Lock size={10} /> {t('visible only to you')}</span>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm" style={{ color: 'var(--text-body)' }}>
              {user.email && <span className="inline-flex items-center gap-1.5"><Mail size={13} style={{ color: 'var(--accent)' }} />{user.email}</span>}
              {user.phone ? (
                <span className="inline-flex items-center gap-1.5 font-mono-nums"><Phone size={13} style={{ color: 'var(--accent)' }} />{user.phone}</span>
              ) : (
                <Link href="/user-profile?tab=settings" className="inline-flex items-center gap-1.5 hover:underline" style={{ color: 'var(--accent)' }}><Phone size={13} />{t('Add phone number')}</Link>
              )}
            </div>
          </div>
        )}

        {/* Bio */}
        {user.bio && <p className="text-sm leading-relaxed mb-4 max-w-lg" style={{ color: 'var(--text-body)' }}>{user.bio}</p>}

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-4 mb-4 text-sm" style={{ color: 'var(--text-muted)' }}>
          {user.location && <span className="flex items-center gap-1.5"><MapPin size={13} />{user.location}</span>}
          {user.website && (
            <a href={user.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 transition-colors duration-100" style={{ color: 'var(--accent)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--accent)')}>
              <LinkIcon size={13} />{user.website.replace(/^https?:\/\//, '')}
            </a>
          )}
          <span className="flex items-center gap-1.5"><Calendar size={13} />{t('Joined')} {user.joinedDate}</span>
          {user.tradingStyle && <span className="flex items-center gap-1.5"><Zap size={13} />{user.tradingStyle}</span>}
        </div>

        {/* Social links — public */}
        {SOCIALS.some((s) => user.socials?.[s.key]) && (
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {SOCIALS.filter((s) => user.socials?.[s.key]).map(({ key, label, Icon, base }) => (
              <a
                key={key}
                href={socialUrl(user.socials![key]!, base)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-100"
                style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                title={label}
              >
                <Icon size={13} />
                {label}
              </a>
            ))}
          </div>
        )}

        {/* Favorite markets */}
        {favoriteMarkets.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="text-xs" style={{ color: 'var(--text-dim)' }}>{t('Markets:')}</span>
            {favoriteMarkets.map((m) => (
              <span key={m} className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'var(--info-muted)', color: 'var(--info)', border: '1px solid transparent' }}>
                {m}
              </span>
            ))}
          </div>
        )}

        {/* Performance score bar */}
        {score > 0 && (
          <div className="mb-5 p-3 rounded-xl" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{t('POSTED-IDEA SCORE')}</span>
              <span className="text-sm font-bold font-mono" style={{ color: getScoreColor(score) }}>{score.toFixed(0)} / 100</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(100, score)}%`, background: `linear-gradient(90deg, ${getScoreColor(score)}, ${getScoreColor(score)}cc)` }} />
            </div>
            <div className="flex items-center gap-4 mt-2">
              {avgRR > 0 && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Avg RR:')} <span className="font-mono" style={{ color: avgRR >= 2 ? 'var(--bullish)' : 'var(--win)' }}>{avgRR.toFixed(1)}R</span></span>}
              {consistency > 0 && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Consistency:')} <span className="font-mono" style={{ color: 'var(--info)' }}>{consistency.toFixed(0)}%</span></span>}
            </div>
          </div>
        )}

        {/* XP & Level Progress */}
        {(user.xp !== undefined || user.level !== undefined) && (
          <div className="mb-5 p-4 rounded-xl" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{t('Trader Level')}</span>
              {user.streakDays !== undefined && user.streakDays > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: 'var(--win-muted)', border: '1px solid var(--win)' }}>
                  <Flame size={12} style={{ color: 'var(--win)' }} />
                  <span className="text-xs font-bold" style={{ color: 'var(--win)' }}>{user.streakDays}{t('-day streak')}</span>
                </div>
              )}
            </div>
            <XpProgressBar xp={user.xp ?? 0} level={user.level ?? 1} />
          </div>
        )}

        {/* Hero stats row */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            {
              label: t('Idea Win Rate'),
              value: `${user.stats.winRate}%`,
              color: user.stats.winRate >= 60 ? 'var(--bullish)' : user.stats.winRate >= 45 ? 'var(--win)' : 'var(--bearish)',
              bg: user.stats.winRate >= 60 ? 'var(--bullish-muted)' : user.stats.winRate >= 45 ? 'var(--win-muted)' : 'var(--bearish-muted)',
              border: user.stats.winRate >= 60 ? 'var(--bullish)' : user.stats.winRate >= 45 ? 'var(--win)' : 'var(--bearish)',
            },
            {
              label: t('Posted Ideas'),
              value: user.stats.totalTrades.toLocaleString(),
              color: 'var(--text-primary)',
              bg: 'var(--bg-sunken)',
              border: 'var(--border)',
            },
            {
              label: t('Idea Avg R:R'),
              value: avgRR > 0 ? `${avgRR.toFixed(1)}R` : '—',
              color: avgRR >= 2 ? 'var(--bullish)' : 'var(--win)',
              bg: 'var(--bg-sunken)',
              border: 'var(--border)',
            },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl p-3 text-center" style={{ background: stat.bg, border: `1px solid ${stat.border}` }}>
              <div className="text-2xl font-bold font-mono mb-0.5" style={{ color: stat.color }}>{stat.value}</div>
              <div className="text-xs" style={{ color: 'var(--text-dim)' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Social stats row — Friends = mutual follows */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {[
            { label: t('Followers'), value: followerCount >= 1000 ? `${(followerCount / 1000).toFixed(1)}K` : followerCount, color: 'var(--text-primary)' },
            { label: t('Following'), value: user.stats.following, color: 'var(--text-primary)' },
            { label: t('Friends'), value: (user.stats as any).friends ?? 0, color: 'var(--accent)' },
            { label: t('Likes'), value: user.stats.likesReceived >= 1000 ? `${(user.stats.likesReceived / 1000).toFixed(1)}K` : user.stats.likesReceived, color: 'var(--text-primary)' },
          ].map((stat) => (
            <div key={stat.label} className="profile-stat-card">
              <div className="text-lg font-bold font-mono mb-0.5" style={{ color: stat.color }}>{stat.value}</div>
              <div className="text-xs" style={{ color: 'var(--text-dim)' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Detailed performance breakdown */}
        {(user.stats.winCount !== undefined || user.stats.lossCount !== undefined) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {[
              { label: t('Wins'), value: user.stats.winCount ?? 0, color: 'var(--bullish)', bg: 'var(--bullish-muted)', border: 'var(--bullish)' },
              { label: t('Losses'), value: user.stats.lossCount ?? 0, color: 'var(--bearish)', bg: 'var(--bearish-muted)', border: 'var(--bearish)' },
              { label: t('Break Even'), value: user.stats.beCount ?? 0, color: 'var(--win)', bg: 'var(--win-muted)', border: 'var(--win)' },
              { label: t('Closed'), value: user.stats.closedTrades ?? 0, color: 'var(--text-secondary)', bg: 'var(--bg-sunken)', border: 'var(--border)' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl p-3 text-center" style={{ background: s.bg, border: `1px solid ${s.border}` }}>
                <div className="text-lg font-bold font-mono" style={{ color: s.color }}>{s.value}</div>
                <div className="text-xs" style={{ color: 'var(--text-dim)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Competitive signals row: Battle record */}
        {(user.stats.battlesWon ?? 0) + (user.stats.battlesLost ?? 0) > 0 && (
          <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--bearish-muted)' }}>
              <Swords size={15} style={{ color: 'var(--bearish)' }} />
            </div>
            <div>
              <div className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{t('Battle Record')}</div>
              <div className="text-sm font-bold font-mono" style={{ color: (user.stats.battleWinRate ?? 0) >= 60 ? 'var(--bullish)' : 'var(--win)' }}>
                {user.stats.battlesWon ?? 0}W / {user.stats.battlesLost ?? 0}L
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}